"use client";

import { useState } from "react";
import Image from "next/image";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { toWebp } from "@/utils/common";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

interface VerifiedClutchReviewsProps {
  data?: any;
}

export default function VerifiedClutchReviews({
  data,
}: VerifiedClutchReviewsProps) {
  const reviews = data?.cards || [];
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const autoplay = (slider: any) => {
    let timeout: ReturnType<typeof setTimeout>;
    let mouseOver = false;

    const clearNextTimeout = () => {
      clearTimeout(timeout);
    };

    const nextTimeout = () => {
      clearTimeout(timeout);

      if (mouseOver) return;

      timeout = setTimeout(() => {
        if (slider.track.details) {
          slider.next();
        }
      }, 3000);
    };

    slider.on("created", () => {
      slider.container.addEventListener("mouseover", () => {
        mouseOver = true;
        clearNextTimeout();
      });

      slider.container.addEventListener("mouseout", () => {
        mouseOver = false;
        nextTimeout();
      });

      nextTimeout();
    });

    slider.on("dragStarted", clearNextTimeout);
    slider.on("animationEnded", nextTimeout);
    slider.on("updated", nextTimeout);
    slider.on("destroyed", clearNextTimeout);
  };

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: true,
      mode: "snap",

      created() {
        setLoaded(true);
      },

      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },

      slides: {
        perView: 1,
        spacing: 24,
      },

      breakpoints: {
        "(min-width:0px)": {
          slides: {
            perView: 1,
            spacing: 16,
          },
        },

        "(min-width:640px)": {
          slides: {
            perView: 1,
            spacing: 10,
          },
        },

        "(min-width:646px)": {
          slides: {
            perView: 1.9,
            spacing: 24,
          },
        },

        "(min-width:768px)": {
          slides: {
            perView: 2.2,
            spacing: 24,
          },
        },

        "(min-width:880px)": {
          slides: {
            perView: 2.5,
            spacing: 24,
          },
        },

        "(min-width:1024px)": {
          slides: {
            perView: 2.5,
            spacing: 30,
          },
        },

        "(min-width:1200px)": {
          slides: {
            perView: 3.1,
            spacing: 30,
          },
        },

        "(min-width:1440px)": {
          slides: {
            perView: 3.3,
            spacing: 30,
          },
        },

        "(min-width:1600px)": {
          slides: {
            perView: 3.5,
            spacing: 30,
          },
        },
      },
    },
    [autoplay],
  );

  return (
    <section className="bg-white xl:py-[100px] lg:py-[80px] py-[56px]">
      <div className="container sm:!pr-0">
        <h2 className="xl:mb-[10px] mb-[4px] text-center xl:text-[48px] xl:leading-[62.4px] lg:text-[40px] lg:leading-[52px] text-[30px] leading-[39px] tracking-[1px] font-bold text-black">
          {data?.title}
        </h2>

        {/* Slider */}
        <div className="">
          <div
            ref={sliderRef}
            className="keen-slider !overflow-x-hidden !overflow-y-visible pt-[30px] mb-[60px]   shadow-[0px_39px_88.7px_0px_#0086E61F] "
          >
            {reviews.map((item: any, index: number) => {
              const avatarUrl = item?.reviewer_image?.data?.attributes?.url;
              const clutchLogoUrl = item?.clutch_image?.data?.attributes?.url;
              const rating = item?.rating ?? 5;

              return (
                <div
                  key={item.id ?? index}
                  className="keen-slider__slide !overflow-visible flex justify-center bg-white w-[380px] h-[540px] shadow-[0px_39px_88.7px_0px_#0086E61F] "
                >
                  <div className="flex h-[540px] w-full flex-col overflow-hidden bg-white p-[40px_24px_24px_24px]  border border-r-1 border-b-1 border-[#c6d6e247] shadow-[0px_39px_88.7px_0px_#0089eb00]">
                    {/* Content */}
                    <div className="flex-1">
                      {/* Project */}
                      <h4 className="xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] tracking-[1px] font-bold text-black mb-[16px]">
                        Project
                      </h4>

                      <p className="xl:mb-[40px] mb-[32px] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] tracking-[1px] font-regular text-secondary-text line-clamp-2">
                        {item?.project_name}
                      </p>

                      {/* Review */}
                      <h4 className="xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] tracking-[1px] font-bold text-black mb-[16px]">
                        Review
                      </h4>

                      <p className="xl:mb-[24px] mb-[30px] xl:text-[18px] xl:leading-[25.2px] text-[16px] leading-[22.4px] tracking-[1px] font-regular text-secondary-text line-clamp-5">
                        {item?.review}
                      </p>
                    </div>

                    {/* Reviewer */}
                    <div className="mb-[24px]">
                      <div className="flex items-center gap-[16px]">
                        <Image
                          src={
                            avatarUrl
                              ? toWebp(`${baseUrl}${avatarUrl}`)
                              : "/images/placeholder.png"
                          }
                          alt={item?.reviewer_name || "Reviewer"}
                          width={67}
                          height={67}
                          className="object-cover w-[67px] h-[67px] object-cover"
                        />

                        <div>
                          <h5 className="xl:text-[16px] x:leading-[22.4px] text-[14px] leading-[19.6px] font-bold text-black mb-[4px] ">
                            {item?.reviewer_name}
                          </h5>

                          <p className="mt-1 text-[14px] leading-[19.6px] text-[#83858C] font-regular">
                            {item?.reviewer_position}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between border-t border-[#B0D9F7] pt-[22px]">
                      <Image
                        src={
                          clutchLogoUrl
                            ? toWebp(`${baseUrl}${clutchLogoUrl}`)
                            : "/images/clutch-landing/clutch-logo.svg"
                        }
                        alt="Clutch"
                        width={70}
                        height={20}
                      />

                      <div className="flex items-center gap-1">
                        {Array.from({ length: rating }).map((_, star) => (
                          <Image
                            key={star}
                            src="/images/clutch-landing/star.svg"
                            alt="Star"
                            width={24}
                            height={24}
                            unoptimized
                            quality={100}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pagination */}
        {loaded && instanceRef.current && (
          <div className="flex justify-center gap-3">
            {reviews.map((_: any, idx: number) => (
              <button
                key={idx}
                onClick={() => instanceRef.current?.moveToIdx(idx)}
                className={`transition-all duration-300 cursor-pointer ${
                  currentSlide === idx
                    ? "h-[4px] w-[60px] rounded-full bg-gradient-to-r from-[#8ABC00] to-[#0086E6]"
                    : "h-[4px] w-[16px] rounded-full bg-[#D7D7D7]"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
